// src/components/analytics/CompletionAnalysis.tsx
/**
 * Sezione analytics dei tempi di completamento (giorni = CompletedAt - CreatedAt).
 * Sorgente dati: useCompletionRequests (solo richieste chiuse, filtrate per data di
 * completamento) più usePreCampionaturaRequests per la serie "Pre-Campionatura" (righe di
 * Campionatura che hanno completato quella fase, filtrate per data fine Pre-Campionatura).
 * Per la Campionatura, i giorni delle richieste chiuse mostrano solo la fase Campione
 * (CompletedAt - PreCampionaturaCompletedAt); le richieste legacy senza questa fase
 * mostrano il tempo totale, invariato. Contiene:
 *  - barre ordinate per singola richiesta (click -> dettaglio in nuova tab);
 *  - tempo medio per serie/tipologia (click sulla barra -> richieste che la compongono);
 *  - segmenti: fasce a giorni (Bozzetto/Campionatura) o percentili (Produzione/Gamma/Pre-Campionatura),
 *    click su una fascia -> richieste;
 *  - andamento mensile della media per serie (tab), click su un mese -> richieste.
 * Tutti i filtri per data completamento sono locali a questa sezione.
 */

import React from 'react';
import {
  Card,
  Grid,
  Group,
  Stack,
  Title,
  Text,
  Select,
  Divider,
  Skeleton,
  Paper,
  Badge,
  UnstyledButton,
  Center,
  Box,
  Tabs,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconClockHour4, IconChartBar, IconTrendingUp } from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  LabelList,
} from 'recharts';
import { RequestType, RequestAnalyticsRow } from '../../types/api';
import { useCompletionRequests, usePreCampionaturaRequests } from '../../hooks/use-analytics-requests';
import { RequestsDrillModal } from './RequestsDrillModal';
import { SERIES_META, ALL_SERIES, seriesLabel, requestDetailPath, SeriesKey } from './analytics-meta';

const TOP_N_OPTIONS = [
  { value: '10', label: 'Top 10' },
  { value: '15', label: 'Top 15' },
  { value: '25', label: 'Top 25' },
  { value: '50', label: 'Top 50' },
  { value: 'all', label: 'Tutte' },
];

const PERCENTILES = [0.5, 0.8, 0.9, 0.95];

/**
 * Fasce a giorni (cumulative "entro N gg" + una "oltre") per serie. Sostituiscono i
 * percentili per Bozzetto e Campionatura; Produzione, Gamma e Pre-Campionatura restano
 * a percentili (nessuna soglia ancora concordata con l'ufficio per la Pre-Campionatura).
 */
const DAY_BUCKETS: Partial<Record<SeriesKey, number[]>> = {
  [RequestType.Bozzetto]: [7, 14, 21],
  [RequestType.Campionatura]: [21, 28, 35],
};

/** Riga di analytics arricchita con la serie di appartenenza (tipologia, o 'pre' per la fase
 * di Pre-Campionatura estratta dalla Campionatura). Per le righe 'pre', durationDays e
 * completedAt sono già sostituiti con quelli della fase (vedi useSeriesRows più sotto). */
type SeriesRow = RequestAnalyticsRow & { series: SeriesKey };

/** Singola riga di un segmento (fascia a giorni o percentile), già pronta per il drill. */
interface SegmentBand {
  key: string;
  /** Porzione in grassetto a sinistra (es. "50%"); assente per le fasce a giorni. */
  leftStrong?: string;
  /** Testo normale a sinistra (es. "entro" oppure "Entro 7 gg"). */
  leftPlain: string;
  /** Valore a destra: "X gg" per i percentili, il conteggio per le fasce a giorni. */
  right: string;
  matching: SeriesRow[];
  drillTitle: string;
  drillSubtitle: string;
}

interface SegmentGroup {
  series: SeriesKey;
  count: number;
  bands: SegmentBand[];
}

/** Percentile con metodo nearest-rank, allineato al backend (Percentile in RequestRepository). */
function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.min(Math.ceil(p * sortedAsc.length) - 1, sortedAsc.length - 1);
  return sortedAsc[Math.max(idx, 0)];
}

/** Chiave mese "YYYY-MM" da una data ISO (usa il fuso locale, come le altre date della UI). */
function monthKey(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Etichetta compatta del mese, es. "2025-01" -> "Gen 25". */
function formatMonthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  const s = new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'short', year: '2-digit' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function openInNewTab(id: number) {
  window.open(requestDetailPath(id), '_blank', 'noopener,noreferrer');
}

interface DrillState {
  opened: boolean;
  title: string;
  subtitle?: string;
  rows: RequestAnalyticsRow[];
}

export function CompletionAnalysis() {
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [rankSeries, setRankSeries] = React.useState<SeriesKey | undefined>(undefined);
  const [topN, setTopN] = React.useState<string>('15');

  const [drill, setDrill] = React.useState<DrillState>({
    opened: false,
    title: '',
    rows: [],
  });

  const openDrill = (title: string, rows: RequestAnalyticsRow[], subtitle?: string) =>
    setDrill({ opened: true, title, subtitle, rows });
  const closeDrill = () => setDrill((d) => ({ ...d, opened: false }));

  const dateRange = {
    startDate: startDate ? startDate.toISOString() : undefined,
    endDate: endDate ? endDate.toISOString() : undefined,
  };

  const { data: rows, isLoading: isLoadingCompletion } = useCompletionRequests(dateRange);
  const { data: preRows, isLoading: isLoadingPre } = usePreCampionaturaRequests(dateRange);
  const isLoading = isLoadingCompletion || isLoadingPre;

  // Righe per i grafici: le richieste chiuse (per tipologia; per la Campionatura solo la
  // fase Campione) più le righe "pre" (Campionatura che ha completato la Pre-Campionatura,
  // con durationDays/completedAt sostituiti da quelli della fase).
  const allRows = React.useMemo<SeriesRow[]>(() => {
    const completion: SeriesRow[] = (rows ?? [])
      .filter((r) => r.durationDays != null)
      .map((r) => ({
        ...r,
        series: r.type as SeriesKey,
        durationDays: r.type === RequestType.Campionatura ? (r.campionaturaDurationDays ?? r.durationDays) : r.durationDays,
      }));

    const pre: SeriesRow[] = (preRows ?? [])
      .filter((r) => r.preCampionaturaDurationDays != null)
      .map((r) => ({
        ...r,
        series: 'pre' as SeriesKey,
        durationDays: r.preCampionaturaDurationDays,
        completedAt: r.preCampionaturaCompletedAt,
      }));

    return [...completion, ...pre];
  }, [rows, preRows]);

  // Serie effettivamente presenti fra le richieste completate
  const presentSeries = React.useMemo(
    () => ALL_SERIES.filter((s) => allRows.some((r) => r.series === s)),
    [allRows]
  );

  // --- Barre ordinate per singola richiesta ---
  const rankedRows = React.useMemo(() => {
    const filtered = rankSeries != null ? allRows.filter((r) => r.series === rankSeries) : allRows;
    const sorted = [...filtered].sort((a, b) => (b.durationDays ?? 0) - (a.durationDays ?? 0));
    const limited = topN === 'all' ? sorted : sorted.slice(0, Number(topN));
    return limited.map((r) => ({
      id: r.id,
      name: r.progressiveNumberFormatted,
      days: r.durationDays ?? 0,
      series: r.series,
      clientName: r.clientName,
      title: r.title,
    }));
  }, [allRows, rankSeries, topN]);

  // --- Tempo medio per serie ---
  const avgData = React.useMemo(
    () =>
      presentSeries.map((s) => {
        const durs = allRows.filter((r) => r.series === s).map((r) => r.durationDays as number);
        const avg = durs.length ? durs.reduce((a, b) => a + b, 0) / durs.length : 0;
        return { series: s, name: seriesLabel(s), avg: Math.round(avg * 10) / 10, count: durs.length };
      }),
    [allRows, presentSeries]
  );

  // --- Segmenti per serie: fasce a giorni (Bozzetto/Campionatura) o percentili (resto) ---
  const segments = React.useMemo<SegmentGroup[]>(
    () =>
      presentSeries.map((s) => {
        const seriesRows = allRows.filter((r) => r.series === s);
        const durs = seriesRows.map((r) => r.durationDays as number).sort((a, b) => a - b);
        const count = seriesRows.length;
        const buckets = DAY_BUCKETS[s];

        // Bozzetto / Campionatura: % cumulativa di chiuse entro N giorni; l'ultima fascia
        // ("oltre") chiude la cumulata e vale sempre 100% (tutte le chiuse).
        if (buckets) {
          const pctOf = (n: number) => (count > 0 ? Math.round((n / count) * 100) : 0);
          const bands: SegmentBand[] = buckets.map((th) => {
            const matching = seriesRows.filter((r) => (r.durationDays as number) <= th);
            const pct = pctOf(matching.length);
            return {
              key: `le-${th}`,
              leftPlain: `Entro ${th} gg`,
              right: `${pct}%`,
              matching,
              drillTitle: `${seriesLabel(s)} — chiuse entro ${th} gg (${pct}%)`,
              drillSubtitle: `${matching.length} ${
                matching.length === 1 ? 'richiesta chiusa' : 'richieste chiuse'
              } su ${count} entro ${th} giorni`,
            };
          });
          const last = buckets[buckets.length - 1];
          bands.push({
            key: 'all',
            leftPlain: `Oltre ${last} gg`,
            right: '100%',
            matching: seriesRows,
            drillTitle: `${seriesLabel(s)} — tutte le chiuse (100%)`,
            drillSubtitle: `${count} ${count === 1 ? 'richiesta chiusa' : 'richieste chiuse'} in totale`,
          });
          return { series: s, count, bands };
        }

        // Produzione / Gamma / Pre-Campionatura: percentili di completamento (invariato).
        const bands: SegmentBand[] = PERCENTILES.map((p) => {
          const days = percentile(durs, p);
          const pct = Math.round(p * 100);
          const rounded = Math.round(days);
          const matching = seriesRows.filter((r) => (r.durationDays as number) <= days);
          return {
            key: `p-${p}`,
            leftStrong: `${pct}%`,
            leftPlain: 'entro',
            right: `${rounded} gg`,
            matching,
            drillTitle: `${seriesLabel(s)} — ${pct}% completato entro ${rounded} gg`,
            drillSubtitle: `${matching.length} ${
              matching.length === 1 ? 'richiesta completata' : 'richieste completate'
            } entro ${rounded} giorni`,
          };
        });
        return { series: s, count, bands };
      }),
    [allRows, presentSeries]
  );

  const rankHeight = Math.max(260, rankedRows.length * 26 + 40);
  const hasData = allRows.length > 0;

  return (
    <Card withBorder p="md">
      <Group justify="space-between" align="flex-end" mb="md" wrap="wrap">
        <div>
          <Group gap={8}>
            <IconClockHour4 size={22} />
            <Title order={3}>Tempi di completamento</Title>
          </Group>
          <Text c="dimmed" size="sm">
            Giorni tra creazione e completamento delle richieste chiuse. Ogni grafico è
            cliccabile: apre le richieste che lo compongono.
          </Text>
        </div>
        <Group gap="sm" wrap="wrap">
          <DatePickerInput
            label="Completate dal"
            placeholder="Data inizio"
            value={startDate}
            onChange={setStartDate as any}
            clearable
            w={170}
          />
          <DatePickerInput
            label="Completate al"
            placeholder="Data fine"
            value={endDate}
            onChange={setEndDate as any}
            clearable
            minDate={startDate ?? undefined}
            w={170}
          />
        </Group>
      </Group>

      {isLoading ? (
        <Skeleton height={320} />
      ) : !hasData ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconChartBar size={32} opacity={0.5} />
            <Text c="dimmed">Nessuna richiesta completata nel periodo selezionato.</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="lg">
          <Grid>
            {/* Tempo medio per serie (tipologia + Pre-Campionatura) */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Paper withBorder p="md" h="100%">
                <Title order={5} mb={4}>
                  Tempo medio per tipologia
                </Title>
                <Text size="xs" c="dimmed" mb="sm">
                  Giorni medi di completamento (per la Campionatura, solo fase Campione).
                  Clicca una barra per le richieste.
                </Text>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={avgData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} unit=" gg" width={48} />
                    <Tooltip
                      formatter={(v: any) => [`${v} gg`, 'Media']}
                      labelFormatter={(l) => String(l)}
                    />
                    <Bar
                      dataKey="avg"
                      cursor="pointer"
                      onClick={(d: any) => {
                        const s = (d?.series ?? d?.payload?.series) as SeriesKey;
                        if (s == null) return;
                        openDrill(
                          `${seriesLabel(s)} — richieste completate`,
                          allRows.filter((r) => r.series === s),
                          `${allRows.filter((r) => r.series === s).length} richieste chiuse`
                        );
                      }}
                    >
                      {avgData.map((e) => (
                        <Cell key={e.series} fill={SERIES_META[e.series]?.color ?? '#888'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid.Col>

            {/* Segmenti percentili */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Paper withBorder p="md" h="100%">
                <Title order={5} mb={4}>
                  Segmenti — tempi di completamento
                </Title>
                <Text size="xs" c="dimmed" mb="sm">
                  Bozzetti e Campionatura: quota cumulativa chiusa entro le soglie di giorni;
                  Produzione e Gamma a percentili. Clicca una fascia per vedere le richieste.
                </Text>
                <SimpleSegmentGrid segments={segments} openDrill={openDrill} />
              </Paper>
            </Grid.Col>
          </Grid>

          <Divider />

          {/* Andamento mensile della media (con tab per tipologia/Pre-Campionatura) */}
          <MonthlyAveragesTrend
            allRows={allRows}
            presentSeries={presentSeries}
            openDrill={openDrill}
          />

          <Divider />

          {/* Barre ordinate per singola richiesta */}
          <div>
            <Group justify="space-between" align="flex-end" mb="xs" wrap="wrap">
              <div>
                <Title order={5}>Tempo per singola richiesta</Title>
                <Text size="xs" c="dimmed">
                  Ordinate dalla più lunga alla più breve. Clicca una barra per aprire il
                  dettaglio in una nuova tab.
                </Text>
              </div>
              <Group gap="sm">
                <Select
                  label="Tipologia"
                  placeholder="Tutte"
                  clearable
                  w={170}
                  data={presentSeries.map((s) => ({ value: String(s), label: seriesLabel(s) }))}
                  value={rankSeries != null ? String(rankSeries) : null}
                  onChange={(v) => setRankSeries(v ? (v === 'pre' ? 'pre' : (Number(v) as RequestType)) : undefined)}
                />
                <Select
                  label="Mostra"
                  w={130}
                  data={TOP_N_OPTIONS}
                  value={topN}
                  onChange={(v) => setTopN(v ?? '15')}
                  allowDeselect={false}
                />
              </Group>
            </Group>
            <ResponsiveContainer width="100%" height={rankHeight}>
              <BarChart
                layout="vertical"
                data={rankedRows}
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} unit=" gg" />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={72}
                  tick={{ fontSize: 11 }}
                  interval={0}
                />
                <Tooltip content={<RankedTooltip />} />
                <Bar
                  dataKey="days"
                  cursor="pointer"
                  onClick={(d: any) => {
                    const id = d?.id ?? d?.payload?.id;
                    if (id != null) openInNewTab(Number(id));
                  }}
                >
                  {rankedRows.map((e) => (
                    <Cell key={e.id} fill={SERIES_META[e.series]?.color ?? '#888'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Stack>
      )}

      <RequestsDrillModal
        opened={drill.opened}
        onClose={closeDrill}
        title={drill.title}
        subtitle={drill.subtitle}
        rows={drill.rows}
      />
    </Card>
  );
}

/** Griglia dei segmenti per tipologia con righe cliccabili (fasce a giorni o percentili). */
function SimpleSegmentGrid({
  segments,
  openDrill,
}: {
  segments: SegmentGroup[];
  openDrill: (title: string, rows: RequestAnalyticsRow[], subtitle?: string) => void;
}) {
  if (segments.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        Nessun dato disponibile.
      </Text>
    );
  }

  return (
    <Grid>
      {segments.map((seg) => (
        <Grid.Col key={seg.series} span={{ base: 12, sm: 6 }}>
          <Paper withBorder radius="md" p="sm">
            <Group justify="space-between" mb={6}>
              <Badge color={SERIES_META[seg.series]?.mantineColor ?? 'gray'} variant="light">
                {seriesLabel(seg.series)}
              </Badge>
              <Text size="xs" c="dimmed">
                {seg.count} chiuse
              </Text>
            </Group>
            <Stack gap={2}>
              {seg.bands.map((b) => (
                <UnstyledButton
                  key={b.key}
                  onClick={() => openDrill(b.drillTitle, b.matching, b.drillSubtitle)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 8px',
                    borderRadius: 6,
                  }}
                  className="segment-row"
                >
                  <Text size="sm">
                    {b.leftStrong && (
                      <>
                        <Text span fw={700}>
                          {b.leftStrong}
                        </Text>{' '}
                      </>
                    )}
                    {b.leftPlain}
                  </Text>
                  <Text size="sm" fw={600}>
                    {b.right}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          </Paper>
        </Grid.Col>
      ))}
    </Grid>
  );
}

/**
 * Andamento mensile del tempo medio di completamento: una linea con il valore per ogni
 * mese; le tab passano da una tipologia all'altra senza dover impostare filtri. Usa gli
 * stessi dati (filtrati per data di completamento) della sezione: senza filtri mostra
 * tutti i mesi. Clic su un punto -> richieste completate in quel mese.
 */
function MonthlyAveragesTrend({
  allRows,
  presentSeries,
  openDrill,
}: {
  allRows: SeriesRow[];
  presentSeries: SeriesKey[];
  openDrill: (title: string, rows: RequestAnalyticsRow[], subtitle?: string) => void;
}) {
  const [activeSeries, setActiveSeries] = React.useState<SeriesKey>(presentSeries[0]);

  // Mantiene la tab attiva valida se cambia l'insieme delle serie presenti.
  React.useEffect(() => {
    if (presentSeries.length > 0 && !presentSeries.includes(activeSeries)) {
      setActiveSeries(presentSeries[0]);
    }
  }, [presentSeries, activeSeries]);

  const monthlyData = React.useMemo(() => {
    const map = new Map<string, { sum: number; count: number }>();
    for (const r of allRows) {
      if (r.series !== activeSeries || r.durationDays == null) continue;
      const key = monthKey(r.completedAt);
      if (!key) continue;
      const cur = map.get(key) ?? { sum: 0, count: 0 };
      cur.sum += r.durationDays;
      cur.count += 1;
      map.set(key, cur);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, v]) => ({
        key,
        label: formatMonthLabel(key),
        avg: Math.round((v.sum / v.count) * 10) / 10,
        count: v.count,
      }));
  }, [allRows, activeSeries]);

  const color = SERIES_META[activeSeries]?.color ?? '#228BE6';
  const showLabels = monthlyData.length > 0 && monthlyData.length <= 24;

  const drillMonth = (key: string, label: string, avg: number) => {
    const rows = allRows.filter((r) => r.series === activeSeries && monthKey(r.completedAt) === key);
    openDrill(
      `${seriesLabel(activeSeries)} — ${label}`,
      rows,
      `${rows.length} ${
        rows.length === 1 ? 'richiesta completata' : 'richieste completate'
      } · media ${avg} gg`
    );
  };

  return (
    <div>
      <div>
        <Group gap={6}>
          <IconTrendingUp size={18} />
          <Title order={5}>Andamento mensile della media</Title>
        </Group>
        <Text size="xs" c="dimmed">
          Giorni medi di completamento mese per mese. Clicca un punto per le richieste di
          quel mese.
        </Text>
      </div>

      <Tabs
        value={String(activeSeries)}
        onChange={(v) => v && setActiveSeries(v === 'pre' ? 'pre' : (Number(v) as RequestType))}
        my="sm"
      >
        <Tabs.List>
          {presentSeries.map((s) => (
            <Tabs.Tab key={s} value={String(s)} color={SERIES_META[s]?.mantineColor}>
              {seriesLabel(s)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {monthlyData.length === 0 ? (
        <Center py="lg">
          <Text c="dimmed" size="sm">
            Nessuna richiesta completata per questa tipologia nel periodo.
          </Text>
        </Center>
      ) : (
        <Box style={{ cursor: 'pointer' }}>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={monthlyData}
              margin={{ top: 24, right: 24, left: 0, bottom: 8 }}
              onClick={(state: any) => {
                const p = state?.activePayload?.[0]?.payload;
                if (p) drillMonth(p.key, p.label, p.avg);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis tick={{ fontSize: 12 }} unit=" gg" width={48} />
              <Tooltip content={<MonthlyTooltip />} />
              <Line
                type="monotone"
                dataKey="avg"
                name="Media"
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3, fill: color }}
                activeDot={{ r: 6 }}
                isAnimationActive={false}
              >
                {showLabels && (
                  <LabelList
                    dataKey="avg"
                    position="top"
                    offset={10}
                    fontSize={11}
                    fill="var(--mantine-color-text)"
                  />
                )}
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </div>
  );
}

/** Tooltip custom per l'andamento mensile della media. */
function MonthlyTooltip({ active, payload }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <Box
      style={{
        background: 'var(--mantine-color-body)',
        border: '1px solid var(--mantine-color-gray-4)',
        borderRadius: 6,
        padding: '8px 10px',
        maxWidth: 220,
      }}
    >
      <Text size="sm" fw={700}>
        {d.label}
      </Text>
      <Text size="sm">Media: {d.avg} gg</Text>
      <Text size="xs" c="dimmed">
        {d.count} {d.count === 1 ? 'richiesta' : 'richieste'}
      </Text>
      <Text size="xs" c="blue" mt={2}>
        Clicca per le richieste del mese
      </Text>
    </Box>
  );
}

/** Tooltip custom per le barre della singola richiesta. */
function RankedTooltip({ active, payload }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <Box
      style={{
        background: 'var(--mantine-color-body)',
        border: '1px solid var(--mantine-color-gray-4)',
        borderRadius: 6,
        padding: '8px 10px',
        maxWidth: 260,
      }}
    >
      <Text size="sm" fw={700}>
        {d.name} · {seriesLabel(d.series)}
      </Text>
      {d.clientName && (
        <Text size="xs" c="dimmed" lineClamp={1}>
          {d.clientName}
        </Text>
      )}
      {d.title && (
        <Text size="xs" lineClamp={2}>
          {d.title}
        </Text>
      )}
      <Text size="sm" mt={4}>
        {d.days.toFixed(1)} giorni
      </Text>
      <Text size="xs" c="blue" mt={2}>
        Clicca per aprire in una nuova tab
      </Text>
    </Box>
  );
}
